import {
  fieldProgressSourceDestination,
  fieldProgressStageDestination,
} from './field-progress-navigation';

import type { FieldDestination } from './field-overview';
import type {
  FieldProgressCell,
  FieldProgressStage,
  FieldProgressState,
  FieldSnapshot,
} from '@mio/shared';

const definitions: { stage: FieldProgressStage; label: string; summary: string; title: string }[] =
  [
    {
      stage: 'agreed',
      label: 'Understand',
      summary: 'Agree on the problem and what a good result looks like.',
      title: 'Start with the problem',
    },
    {
      stage: 'designed',
      label: 'Design',
      summary: 'Work through how your solution will fit together.',
      title: 'Shape your solution',
    },
    {
      stage: 'built',
      label: 'Build',
      summary: 'Turn the agreed solution into working code.',
      title: 'Build the solution',
    },
    {
      stage: 'verified',
      label: 'Test',
      summary: 'Show that the client flows work, including when things go wrong.',
      title: 'Check what you’ve built',
    },
    {
      stage: 'controls',
      label: 'Controls',
      summary: 'Pass the security checks and clear any compliance hold.',
      title: 'Pass the security controls',
    },
    {
      stage: 'shipped',
      label: 'Deliver',
      summary: 'Release the work and make sure someone else can run it.',
      title: 'Prepare to hand it over',
    },
  ];

export interface FieldProgressMilestone {
  stage: FieldProgressStage;
  label: string;
  state: FieldProgressState;
  verified: number;
  total: number;
  summary: string;
  destination: FieldDestination;
}

/** A conservative summary of public evidence; it never changes readiness or awards a score. */
function combineStates(states: FieldProgressState[]): FieldProgressState {
  if (!states.length) return 'not_started';
  if (states.every((state) => state === 'verified')) return 'verified';
  if (states.includes('needs_recheck')) return 'needs_recheck';
  const incomplete = states.filter((state) => state !== 'verified');
  if (incomplete.every((state) => state === 'waiting')) return 'waiting';
  if (states.some((state) => state !== 'not_started')) return 'in_progress';
  return 'not_started';
}

export function fieldProgressJourney(snapshot: FieldSnapshot) {
  const progress = snapshot.progress;
  const milestones: FieldProgressMilestone[] = definitions.map(({ stage, label, summary }) => {
    // Handoff documents belong at delivery, not at the start of understanding the problem.
    const groups = (['knowledge', 'trade', 'recovery'] as const).map(
      (id) =>
        progress?.rows.find((row) => row.id === id)?.cells.filter((cell) => cell.stage === stage) ??
        [],
    );
    if (stage === 'shipped') {
      const handoff = progress?.rows.find((row) => row.id === 'handoff');
      // Count operator handoff as one part; all six underlying standards still apply.
      groups.push(
        definitions.flatMap(
          ({ stage: handoffStage }) =>
            handoff?.cells.find((cell) => cell.stage === handoffStage) ?? [],
        ),
      );
    }
    const states = groups.map((cells, index) =>
      stage === 'shipped' && index === 3 && cells.length !== definitions.length
        ? ('not_started' as const)
        : combineStates(cells.map((cell) => cell.state)),
    );
    const state = combineStates(states);
    const cells = groups.flat();
    const relevant =
      cells.find((cell) => cell.state === 'needs_recheck') ??
      cells.find((cell) => cell.state === 'waiting') ??
      cells.find((cell) => cell.state !== 'verified');
    const verified = states.filter((state) => state === 'verified').length;
    const names = [...new Set(cells.flatMap((cell) => cell.waitingOn))].map(
      (id) => snapshot.actors.find((actor) => actor.id === id)?.name.split(' ')[0] ?? id,
    );
    return {
      stage,
      label,
      state,
      verified,
      total: groups.length,
      summary:
        state === 'needs_recheck'
          ? 'Something changed. Check the affected work again.'
          : state === 'waiting'
            ? names.length
              ? `Waiting for ${names.join(' and ')} to confirm the remaining work.`
              : 'The remaining checks are still running.'
            : state === 'verified'
              ? stage === 'shipped'
                ? 'The release and operator handoff have supporting evidence.'
                : 'All three client flows have current supporting evidence for this step.'
              : summary,
      destination: stageDestination(snapshot, stage, relevant),
    };
  });
  const completedCount = milestones.filter((milestone) => milestone.state === 'verified').length;
  const current =
    milestones.find((milestone) => milestone.state === 'needs_recheck') ??
    milestones.find((milestone) => milestone.state !== 'verified') ??
    milestones.at(-1);
  if (!current) throw new Error('The progress path must define at least one milestone.');
  const completed = snapshot.assessment.status === 'completed';
  const ready = progress?.readiness.ready ?? false;
  const title = completed
    ? 'Final submission accepted'
    : ready
      ? 'Ready for final review'
      : completedCount === milestones.length
        ? 'Check the remaining blockers'
        : current.state === 'needs_recheck'
          ? 'Recheck the changed work'
          : (definitions.find((item) => item.stage === current.stage)?.title ?? 'Your progress');
  const description = completed
    ? 'Your work and review are here whenever you need them.'
    : ready
      ? 'Your delivery evidence is ready. You can request the final review.'
      : completedCount === milestones.length
        ? 'Your milestones are confirmed. Open readiness to see what still needs attention.'
        : current.state === 'needs_recheck'
          ? 'Your earlier work is saved. The marked step needs another check.'
          : 'See where you’ve reached. Pick any step to keep going.';
  const delivery = milestones.find((milestone) => milestone.stage === 'shipped');
  if (delivery && (completed || ready || completedCount === milestones.length))
    delivery.destination = { section: 'assessment' };
  return {
    milestones,
    currentStage: completed || ready ? ('shipped' as const) : current.stage,
    completedCount,
    title,
    description,
  };
}

function stageDestination(
  snapshot: FieldSnapshot,
  stage: FieldProgressStage,
  relevant: FieldProgressCell | undefined,
): FieldDestination {
  if (stage === 'built') return { section: 'build' };
  const kind =
    stage === 'agreed'
      ? 'decision'
      : stage === 'designed'
        ? 'document'
        : stage === 'verified'
          ? 'run'
          : stage === 'controls'
            ? 'run'
            : undefined;
  const source = relevant?.evidence.find((item) => item.kind === kind && !item.stale);
  return source
    ? fieldProgressSourceDestination(snapshot, source)
    : fieldProgressStageDestination[stage];
}
